import { isEqual } from "lodash";

import { signalTypeMap } from "app/pages/sales-intelligence/domain/signals/types/signal";
import type { SignalType } from "app/pages/sales-intelligence/domain/signals/types/signal";
import type AdNetworkSignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/ad-network/AdNetworkSignalFilters";
import type EcommerceSignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/ecommerce/EcommerceSignalFilters";
import type IntentSignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/intent/IntentSignalFilters";
import type NewsSignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/news/NewsSignalFilters";
import type {
    FilterNameResolver,
    FiltersEqualityPredicate,
    FilterService,
} from "app/pages/sales-intelligence/sub-modules/signals/models/services/FilterService";
import { FilterType } from "app/pages/sales-intelligence/sub-modules/signals/models/services/FilterService";
import type TechnologySignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/technology/TechnologySignalFilters";
import type TrafficSignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/TrafficSignalFilters";
import { areEqualTimeframes } from "app/pages/sales-intelligence/sub-modules/signals/utils/areEqualTimeframes";
import { normalizeEcommerceFiltersForEquality } from "app/pages/sales-intelligence/sub-modules/signals/utils/ecommerce/ecommerceFilterNormalization";
import { areEqualArrays } from "app/pages/sales-intelligence/utils/arrays";

import createAdNetworksNameFactory from "../../filters/ad-networks/adNetworksFilterNameFactory";
import createCountriesNameFactory from "../../filters/countries/countriesFilterNameFactory";
import createIntentTopicsNameFactory from "../../filters/intent-topics/intentTopicsFilterNameFactory";
import createTechnologiesNameFactory from "../../filters/technologies/technologiesFilterNameFactory";
import createAnyNewsCategoriesNameFactory from "../any-news-categories/anyNewsCategoriesFilterNameFactory";
import createNewsCategoriesNameFactory from "../news-categories/newsCategoriesFilterNameFactory";

const createFilterServiceFactory = (): FilterService => {
    const filterNameResolverMap: Record<FilterType, FilterNameResolver> = {
        [FilterType.Countries]: createCountriesNameFactory(),
        [FilterType.AdNetworks]: createAdNetworksNameFactory(),
        [FilterType.Technologies]: createTechnologiesNameFactory(),
        [FilterType.NewsCategories]: createNewsCategoriesNameFactory(),
        [FilterType.AnyNewsCategories]: createAnyNewsCategoriesNameFactory(),
        [FilterType.IntentTopics]: createIntentTopicsNameFactory(),
    };

    return {
        getFilterName(filterType: FilterType): FilterNameResolver {
            return filterNameResolverMap[filterType];
        },

        getFiltersEqualityPredicate(signalType: SignalType): FiltersEqualityPredicate {
            switch (signalType) {
                case signalTypeMap.adNetwork:
                    return (
                        filters: Readonly<AdNetworkSignalFilters>,
                        comparedFilters: Readonly<AdNetworkSignalFilters>,
                    ) => {
                        if (filters.action !== comparedFilters.action) {
                            return false;
                        }

                        return areEqualArrays(filters.adNetworkIds, comparedFilters.adNetworkIds);
                    };
                case signalTypeMap.technology:
                    return (
                        filters: Readonly<TechnologySignalFilters>,
                        comparedFilters: Readonly<TechnologySignalFilters>,
                    ) => {
                        if (filters.type !== comparedFilters.type) {
                            return false;
                        }

                        if (!areEqualTimeframes(filters, comparedFilters)) {
                            return false;
                        }

                        return (
                            areEqualArrays(filters.technologies, comparedFilters.technologies) &&
                            areEqualArrays(filters.technologyCategories, comparedFilters.technologyCategories) &&
                            areEqualArrays(filters.technologySubCategories, comparedFilters.technologySubCategories)
                        );
                    };
                case signalTypeMap.news:
                    return (filters: Readonly<NewsSignalFilters>, comparedFilters: Readonly<NewsSignalFilters>) => {
                        if (!areEqualTimeframes(filters, comparedFilters)) {
                            return false;
                        }

                        return areEqualArrays(filters.categories, comparedFilters.categories);
                    };
                case signalTypeMap.traffic:
                    return (
                        filters: Readonly<TrafficSignalFilters>,
                        comparedFilters: Readonly<TrafficSignalFilters>,
                    ) => {
                        return isEqual(filters, comparedFilters);
                    };
                case signalTypeMap.ecommerce:
                    return (
                        filters: Readonly<EcommerceSignalFilters>,
                        comparedFilters: Readonly<EcommerceSignalFilters>,
                    ) => {
                        const normalizedA = normalizeEcommerceFiltersForEquality(filters);
                        const normalizedB = normalizeEcommerceFiltersForEquality(comparedFilters);

                        if (normalizedA === null || normalizedB === null) {
                            return false;
                        }

                        return isEqual(normalizedA, normalizedB);
                    };
                case signalTypeMap.intent:
                    return (filters: Readonly<IntentSignalFilters>, comparedFilters: Readonly<IntentSignalFilters>) => {
                        const conditions = [
                            areEqualArrays(filters.topics, comparedFilters.topics),
                            areEqualArrays(filters.countries, comparedFilters.countries),
                        ];

                        return conditions.every(Boolean);
                    };

                case signalTypeMap.advertiserActivity:
                case signalTypeMap.apps:
                case signalTypeMap.roleChanges:
                    return () => true;
                default:
                    throw new Error(`Unknown signal type: ${signalType}`);
            }
        },
    };
};

export default createFilterServiceFactory;
